import { create, StoreApi } from 'zustand';
import { User } from '@/types/auth';
import { authService, isTwoFactorPending } from '@/services/auth';
import { legalService } from '@/services/legal';
import { redirectPersistenceService } from '@/services/redirectPersistence';
import toast from 'react-hot-toast';
import { PlatformService } from '@/utils/platform';

// Legal acceptance pending document
interface LegalPendingDocument {
  type: string;
  version: string;
  url: string;
  title: string;
}

// Two-Factor Authentication pending state
interface TwoFactorPending {
  partialToken: string;
  userId: string;
  methods: ('totp' | 'backup')[];
}

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isInitialized: boolean;
  twoFactorPending: TwoFactorPending | null;
  legalAcceptancePending: boolean;
  legalPendingDocuments: LegalPendingDocument[];
  login: (email: string, password: string) => Promise<{ requiresTwoFactor: boolean }>;
  verify2FA: (token: string, isBackupCode: boolean) => Promise<void>;
  cancelTwoFactor: () => void;
  logout: () => Promise<void>;
  initialize: () => Promise<void>;
  updateUser: (user: User) => void;
  clearLegalAcceptance: () => void;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string | string[]) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  isPractitioner: () => boolean;
  canManageOrg: () => boolean;
  canAccessPlatform: () => boolean;
  isSuperAdmin: () => boolean;
  // New role helpers for RBAC refactor
  isOperator: () => boolean;
  isReadOnly: () => boolean;
  hasClinicalAccess: () => boolean;
  canEditClinical: () => boolean;
}

/**
 * Fetches legal acceptance status and, if any documents are pending,
 * flips the store into "legal gate" mode. Used by login, verify2FA, and
 * initialize so all entry points behave consistently.
 *
 * Fails closed: if the status call errors in a way that suggests pending
 * docs (451), it still opens the gate. Other errors are swallowed to
 * preserve the legacy "table may not exist yet" tolerance.
 */
const checkAndSetLegalAcceptance = async (
  set: StoreApi<AuthState>['setState']
): Promise<void> => {
  try {
    const legalStatus = await legalService.getStatus();
    const pendingDocs = (legalStatus.documents || []).filter((d: any) => !d.accepted);
    if (pendingDocs.length > 0) {
      set({
        legalAcceptancePending: true,
        legalPendingDocuments: pendingDocs.map((d: any) => ({
          type: d.type,
          version: d.version,
          url: d.url,
          title: d.title,
        })),
      });
    }
  } catch (legalError: any) {
    if (
      legalError?.response?.status === 451 &&
      legalError?.response?.data?.pendingDocuments
    ) {
      set({
        legalAcceptancePending: true,
        legalPendingDocuments: legalError.response.data.pendingDocuments,
      });
    }
    // Other errors — allow access (table may not exist yet)
  }
};

export const useAuth = create<AuthState>((set, get) => ({
  user: null,
  isLoading: false,
  isInitialized: false,
  twoFactorPending: null,
  legalAcceptancePending: false,
  legalPendingDocuments: [],

  login: async (email: string, password: string) => {
    set({ isLoading: true });
    try {
      const response = await authService.login({ email, password });

      // Check if 2FA is required
      if (isTwoFactorPending(response)) {
        set({
          isLoading: false,
          twoFactorPending: {
            partialToken: response.partialToken,
            userId: response.userId,
            methods: response.methods,
          },
        });
        return { requiresTwoFactor: true };
      }

      // Normal login success
      set({ user: response.user, isLoading: false, twoFactorPending: null });
      redirectPersistenceService.rememberSignedInUser(response.user.id);

      // Check legal acceptance status immediately so the gate renders
      // before any dashboard API call returns 451.
      await checkAndSetLegalAcceptance(set);

      toast.success(`Welcome back, ${response.user.firstName}!`);
      return { requiresTwoFactor: false };
    } catch (error) {
      set({ isLoading: false });
      const message = error instanceof Error ? error.message : 'Login failed';
      toast.error(message);
      throw error;
    }
  },

  verify2FA: async (token: string, isBackupCode: boolean) => {
    const { twoFactorPending } = get();
    if (!twoFactorPending) {
      throw new Error('No 2FA verification pending');
    }

    set({ isLoading: true });
    try {
      let response;
      if (isBackupCode) {
        response = await authService.verifyBackupCode(twoFactorPending.partialToken, token);
        if (response.warning) {
          toast.error(response.warning, { duration: 6000 });
        }
      } else {
        response = await authService.verify2FA(twoFactorPending.partialToken, token);
      }

      set({ user: response.user, isLoading: false, twoFactorPending: null });
      redirectPersistenceService.rememberSignedInUser(response.user.id);

      // Check legal acceptance status immediately so the gate renders
      // before any dashboard API call returns 451.
      await checkAndSetLegalAcceptance(set);

      toast.success(`Welcome back, ${response.user.firstName}!`);
    } catch (error) {
      set({ isLoading: false });
      const message = error instanceof Error ? error.message : '2FA verification failed';
      throw new Error(message);
    }
  },

  cancelTwoFactor: () => {
    set({ twoFactorPending: null });
  },

  logout: async () => {
    set({ isLoading: true });

    try {
      await authService.logout(); // API call
    } catch (error) {
      console.error('Logout API call failed:', error);
      // Continue with logout even if API fails
    }

    // Clear user state. isLoading stays true until the reload below: rendering the
    // routes with no user would save this page as the next login's redirect (AMP-131).
    set({ user: null });
    redirectPersistenceService.clearRedirectUrl();

    // Clear persisted org context (operationMode) so a connected-org user's mode does
    // not leak to the next user on a shared browser — which would disable standalone
    // patient/appointment creation until a capabilities refresh succeeds. The full
    // reload below rehydrates the store to its default 'standalone'.
    try { localStorage.removeItem('organization-storage'); } catch { /* ignore storage errors */ }

    // Platform-aware navigation
    const loginPath = PlatformService.isNative() ? '/mobile-login' : '/login';
    window.location.href = loginPath; // Force full reload to clear state

    toast.success('Logged out successfully');
  },

  initialize: async () => {
    set({ isLoading: true });
    try {
      if (authService.isAuthenticated()) {
        const user = await authService.getCurrentUser();
        if (user) redirectPersistenceService.rememberSignedInUser(user.id);

        // Check legal acceptance status before unblocking the UI
        try {
          const legalStatus = await legalService.getStatus();
          const pendingDocs = legalStatus.documents?.filter((d: any) => !d.accepted) || [];
          if (pendingDocs.length > 0) {
            set({
              user,
              isLoading: false,
              isInitialized: true,
              legalAcceptancePending: true,
              legalPendingDocuments: pendingDocs.map((d: any) => ({
                type: d.type,
                version: d.version,
                url: d.url,
                title: d.title
              }))
            });
            return;
          }
        } catch (legalError: any) {
          // If 451 returned (shouldn't happen since /legal/ is exempt),
          // extract pending docs from the error response
          if (legalError?.response?.status === 451 && legalError?.response?.data?.pendingDocuments) {
            set({
              user,
              isLoading: false,
              isInitialized: true,
              legalAcceptancePending: true,
              legalPendingDocuments: legalError.response.data.pendingDocuments
            });
            return;
          }
          // Other errors — allow access (table may not exist yet)
        }

        set({ user, isLoading: false, isInitialized: true });
      } else {
        set({ user: null, isLoading: false, isInitialized: true });
      }
    } catch (error) {
      set({ user: null, isLoading: false, isInitialized: true });
    }
  },

  updateUser: (user: User) => {
    set({ user });
    localStorage.setItem('user', JSON.stringify(user));
  },

  clearLegalAcceptance: () => {
    set({ legalAcceptancePending: false, legalPendingDocuments: [] });
  },

  hasPermission: (permission: string) => {
    const { user } = get();
    if (!user) return false;

    // SuperAdmin only has platform and profile permissions (isolated from clinical)
    if (user.role === 'superadmin') {
      return permission.startsWith('platform.') ||
             permission.startsWith('organization.') ||
             permission.startsWith('system.') ||
             permission.startsWith('audit.platform') ||
             permission.startsWith('profile.own');
    }

    // Admin has all permissions except platform controls
    if (user.role === 'admin' && !permission.startsWith('platform.')) return true;

    // Operator has clinical permissions
    if (user.role === 'operator') {
      return permission.startsWith('clinical.') ||
             permission.startsWith('patients.') ||
             permission.startsWith('recordings.') ||
             permission.startsWith('billing.') ||
             permission.startsWith('encounters.') ||
             permission.startsWith('usage.own') ||
             permission.startsWith('profile.own');
    }

    // Staff has read-only permissions
    if (user.role === 'staff') {
      return permission === 'clinical.view' ||
             permission === 'patients.phi.view' ||
             permission === 'recordings.view' ||
             permission === 'billing.codes.view' ||
             permission.startsWith('usage.own') ||
             permission.startsWith('profile.own');
    }

    // Check wildcard permissions
    if (user.permissions.includes('*')) return true;

    // Check exact permission match
    if (user.permissions.includes(permission)) return true;

    // Check wildcard categories (e.g., 'clinical.*' for 'clinical.review')
    const permissionParts = permission.split('.');
    for (let i = 1; i <= permissionParts.length; i++) {
      const wildcardPermission = permissionParts.slice(0, i).join('.') + '.*';
      if (user.permissions.includes(wildcardPermission)) {
        return true;
      }
    }

    return false;
  },

  hasRole: (role: string | string[]) => {
    const { user } = get();
    if (!user) return false;

    const roles = Array.isArray(role) ? role : [role];
    return roles.includes(user.role);
  },

  // Enhanced role checking with hierarchy
  hasAnyRole: (roles: string[]) => {
    const { user } = get();
    if (!user) return false;
    return roles.includes(user.role);
  },

  // Check if user is practitioner (can perform medical reviews)
  // Updated: operator/admin only, NOT superadmin (isolated to platform)
  isPractitioner: () => {
    const { user } = get();
    if (!user) return false;
    return ['operator', 'admin'].includes(user.role);
  },

  // Check if user can manage organization (admin only, NOT superadmin for org management)
  canManageOrg: () => {
    const { user } = get();
    if (!user) return false;
    return user.role === 'admin';
  },

  // Check if user can access platform controls (Management Console)
  // Includes: superadmin (full), enterprise_admin (scoped), channel_partner_admin/partner_admin (scoped)
  canAccessPlatform: () => {
    const { user } = get();
    if (!user) return false;
    const platformRoles = ['superadmin', 'enterprise_admin', 'channel_partner_admin', 'partner_admin'];
    return platformRoles.includes(user.role);
  },

  // Check if user is superadmin (full platform control)
  isSuperAdmin: () => {
    const { user } = get();
    if (!user) return false;
    return user.role === 'superadmin';
  },

  // New: Check if user is operator (clinical power user)
  isOperator: () => {
    const { user } = get();
    if (!user) return false;
    return ['operator', 'admin'].includes(user.role);
  },

  // New: Check if user is read-only (staff)
  isReadOnly: () => {
    const { user } = get();
    if (!user) return false;
    return user.role === 'staff';
  },

  // New: Check if user has clinical access (not superadmin)
  hasClinicalAccess: () => {
    const { user } = get();
    if (!user) return false;
    return ['admin', 'operator', 'staff'].includes(user.role);
  },

  // New: Check if user can edit clinical data (not staff or superadmin)
  canEditClinical: () => {
    const { user } = get();
    if (!user) return false;
    return ['admin', 'operator'].includes(user.role);
  },
}));